import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import duration from "dayjs/plugin/duration";
import { blockTimeList } from "../block-time-config";

dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(duration);

const minutePerMilliSec = 60000;

function _getDate() {
  return new Date();
}

function _toSec(ms: number): string {
  return ms.toString().substring(0, 10);
}

function now(): string {
  return _toSec(_getDate().getTime());
}

function addMinute(minute: number): string {
  const dtNext = _getDate().getTime() + minute * minutePerMilliSec;
  return _toSec(dtNext);
}

function getDate(value?: number | string, format = "") {
  return value ? dayjs(value, format) : dayjs();
}

function period(timeStart: number, timeEnd: number): number {
  const startTime = dayjs.unix(timeStart);
  const endTime = dayjs.unix(timeEnd);
  const period = endTime.diff(startTime, "day", true);
  return period <= 0 ? 0 : Math.ceil(period);
}

function countDownDateTime(
  timeStart: number,
  timeEnd: number,
  format: string
): string {
  const startTime = dayjs.unix(timeStart);
  const endTime = dayjs.unix(timeEnd);
  let millisec = endTime.diff(startTime);
  millisec = millisec > 0 ? millisec : 0;
  return dayjs.duration(millisec).format(format);
}

function getDateByUnix(datetime?: number): dayjs.Dayjs {
  return datetime ? dayjs.unix(datetime) : dayjs();
}
function getUnixFromBlock(
  targetBlock: number | bigint,
  currentBlock: number | bigint
): number {
  // Convert BigInt to number if either of the blocks is a BigInt
  const targetBlockNum =
    typeof targetBlock === "bigint" ? Number(targetBlock) : targetBlock;
  const currentBlockNum =
    typeof currentBlock === "bigint" ? Number(currentBlock) : currentBlock;

  const diffBlock = targetBlockNum - currentBlockNum;
  const isFuture = diffBlock > 0;

  const sortedBlockTimeList = blockTimeList.sort(
    (a, b) => b.changedStartBlock - a.changedStartBlock
  );

  const currentDate = dayjs();
  let remainingBlocks = Math.abs(diffBlock);
  let totalSec = 0;

  for (const blockTime of sortedBlockTimeList) {
    const start = blockTime.changedStartBlock;
    const end = blockTime.changedEndBlock ?? Infinity;

    // Determine if this time period overlaps with the block range we need
    if (isFuture) {
      if (targetBlockNum <= start) continue;
      const rangeStart = Math.max(currentBlockNum, start);
      const rangeEnd = Math.min(targetBlockNum, end);
      const applicableBlocks = Math.max(0, rangeEnd - rangeStart);
      totalSec += applicableBlocks * blockTime.blockTime;
      remainingBlocks -= applicableBlocks;
    } else {
      if (currentBlockNum <= start) continue;
      const rangeStart = Math.max(targetBlockNum, start);
      const rangeEnd = Math.min(currentBlockNum, end);
      const applicableBlocks = Math.max(0, rangeEnd - rangeStart);
      totalSec += applicableBlocks * blockTime.blockTime;
      remainingBlocks -= applicableBlocks;
    }

    if (remainingBlocks <= 0) break;
  }

  if (remainingBlocks > 0 && sortedBlockTimeList.length > 0) {
    totalSec += remainingBlocks * sortedBlockTimeList[0].blockTime;
  }
  const result = isFuture
    ? currentDate.add(totalSec, "second")
    : currentDate.subtract(totalSec, "second");

  return result?.unix() || 0;
}

export {
  now,
  addMinute,
  period,
  getDate,
  getDateByUnix,
  getUnixFromBlock,
  countDownDateTime,
};
